import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Store, Plus } from 'lucide-react';

const Dispensaries = ({ user, onLogout }) => {
  const [dispensaries, setDispensaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [newDispensary, setNewDispensary] = useState({
    name: '',
    address: {
      line1: '',
      city: 'Guadalajara',
      lat: 19.4326,
      lng: -99.1332
    },
    phone: '',
    email: ''
  });

  useEffect(() => {
    fetchDispensaries();
  }, []);

  const fetchDispensaries = async () => {
    try {
      const response = await axios.get('/dispensaries');
      setDispensaries(response.data);
    } catch (error) {
      toast.error('Error al cargar dispensarios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDispensary = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/dispensaries', newDispensary);
      toast.success('¡Dispensario creado!');
      setShowDialog(false);
      fetchDispensaries();
      setNewDispensary({
        name: '',
        address: { line1: '', city: 'Guadalajara', lat: 20.6597, lng: -103.3496 },
        phone: '',
        email: ''
      });
    } catch (error) {
      toast.error('Error al crear dispensario');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} title="Dispensarios">
      <div className="space-y-6" data-testid="dispensaries-container">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#224049' }}>Dispensarios</h1>
            <p className="text-sm" style={{ color: '#666' }}>Gestiona los dispensarios asociados</p>
          </div>
          {(user.role === 'admin' || user.role === 'dispensary_owner') && (
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button style={{ backgroundColor: '#224049', color: '#D4AD6A' }} data-testid="add-dispensary-button">
                  <Plus className="w-4 h-4 mr-2" /> Agregar Dispensario
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Nuevo Dispensario</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateDispensary} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="disp-name">Nombre</Label>
                    <Input
                      id="disp-name"
                      data-testid="dispensary-name-input"
                      value={newDispensary.name}
                      onChange={(e) => setNewDispensary({ ...newDispensary, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="disp-address">Dirección</Label>
                    <Input
                      id="disp-address"
                      data-testid="dispensary-address-input"
                      value={newDispensary.address.line1}
                      onChange={(e) => setNewDispensary({
                        ...newDispensary,
                        address: { ...newDispensary.address, line1: e.target.value }
                      })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="disp-city">Ciudad</Label>
                    <Input
                      id="disp-city"
                      data-testid="dispensary-city-input"
                      value={newDispensary.address.city}
                      onChange={(e) => setNewDispensary({
                        ...newDispensary,
                        address: { ...newDispensary.address, city: e.target.value }
                      })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="disp-phone">Teléfono</Label>
                    <Input
                      id="disp-phone"
                      data-testid="dispensary-phone-input"
                      type="tel"
                      value={newDispensary.phone}
                      onChange={(e) => setNewDispensary({ ...newDispensary, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="disp-email">Email</Label>
                    <Input
                      id="disp-email"
                      data-testid="dispensary-email-input"
                      type="email"
                      value={newDispensary.email}
                      onChange={(e) => setNewDispensary({ ...newDispensary, email: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" style={{ backgroundColor: '#D9A27F', color: 'white' }} data-testid="submit-dispensary-button">
                    Crear Dispensario
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-lg" style={{ color: '#224049' }}>Cargando...</div>
          </div>
        ) : dispensaries.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Store className="w-16 h-16 mx-auto mb-4" style={{ color: '#D9A27F' }} />
              <p className="text-lg" style={{ color: '#666' }}>No hay dispensarios registrados</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dispensaries.map((disp) => (
              <Card key={disp.id} className="card fade-in shadow-lg hover:shadow-xl" data-testid={`dispensary-card-${disp.id}`}>
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="p-3 rounded-full" style={{ backgroundColor: '#224049' }}>
                      <Store className="w-6 h-6" style={{ color: '#D4AD6A' }} />
                    </div>
                    <div>
                      <CardTitle style={{ color: '#224049' }}>{disp.name}</CardTitle>
                      <p className="text-sm" style={{ color: '#666' }}>{disp.email}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <p className="text-xs font-medium" style={{ color: '#666' }}>Dirección</p>
                    <p className="text-sm" style={{ color: '#224049' }}>
                      {disp.address.line1}, {disp.address.city}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium" style={{ color: '#666' }}>Teléfono</p>
                    <p className="text-sm" style={{ color: '#224049' }}>{disp.phone}</p>
                  </div>
                  <div>
                    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${disp.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {disp.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dispensaries;